import React, { useEffect } from "react";
import "./Legal.css";

const SUPPORT_EMAIL =
  process.env.REACT_APP_SUPPORT_EMAIL || "support@matchmixer.co.uk";
const APP_URL = "https://www.matchmixer.co.uk";
const EFFECTIVE_DATE = "10 July 2026";

function PrivacyPolicy() {
  return (
    <div className="legal-body">
      <h2>Privacy Policy</h2>
      <p className="legal-meta">Last updated: {EFFECTIVE_DATE}</p>

      <h3>1. Who we are</h3>
      <p>
        Match Mixer is a free football prediction game available at{" "}
        <a href={APP_URL}>{APP_URL}</a>, operated by an individual trading as
        Match Mixer. When this policy refers to &ldquo;we&rdquo;,
        &ldquo;us&rdquo; or &ldquo;our&rdquo;, it means that individual. You
        can contact us at{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </p>

      <h3>2. What data we collect</h3>
      <ul>
        <li>
          <strong>Account information</strong> — email address and display name,
          provided when you register or sign in via Google.
        </li>
        <li>
          <strong>Predictions</strong> — score predictions you submit for
          fixtures.
        </li>
        <li>
          <strong>League activity</strong> — leagues you create or join,
          including join timestamps.
        </li>
        <li>
          <strong>Notification preferences</strong> — whether you have
          unsubscribed from email notifications.
        </li>
      </ul>
      <p>
        We do <strong>not</strong> collect payment details, location data, or
        any sensitive personal data.
      </p>

      <h3>3. How we use your data</h3>
      <ul>
        <li>
          To provide the Match Mixer service (predictions, leagues, scores).
        </li>
        <li>
          To send you optional email notifications about upcoming fixtures and
          results (you can unsubscribe at any time via the link in any email).
        </li>
        <li>To respond to support requests.</li>
      </ul>

      <h3>4. Legal basis (UK GDPR)</h3>
      <p>
        We process your data on the basis of{" "}
        <strong>legitimate interests</strong> (providing the game service) and,
        for notification emails, <strong>consent</strong> (implied by
        registering and not unsubscribing). You may withdraw consent at any time
        by unsubscribing.
      </p>

      <h3>5. Third-party services</h3>
      <ul>
        <li>
          <strong>Firebase Authentication (Google)</strong> — handles sign-in.
          Your authentication data is governed by{" "}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noreferrer"
          >
            Google&rsquo;s Privacy Policy
          </a>
          .
        </li>
        <li>
          <strong>Resend</strong> — used to send notification emails. Email
          addresses are transmitted to Resend solely for delivery.
        </li>
        <li>
          <strong>Railway</strong> — hosts the backend and database. Data is
          stored on Railway&rsquo;s infrastructure.
        </li>
        <li>
          <strong>Netlify</strong> — hosts the frontend.
        </li>
        <li>
          <strong>API-Football (api-sports.io)</strong> — provides fixture and
          results data. No personal data is shared with this service.
        </li>
      </ul>

      <h3>6. Data retention</h3>
      <p>
        We retain your account data and predictions for as long as your account
        is active. You can request deletion at any time (see Section 8). Email
        notification logs are kept to prevent duplicate sends and are not used
        for any other purpose.
      </p>

      <h3>7. Cookies</h3>
      <p>
        Match Mixer does not use tracking or advertising cookies. Firebase
        Authentication may set a session cookie solely for authentication
        purposes.
      </p>

      <h3>8. Your rights</h3>
      <p>Under UK GDPR you have the right to:</p>
      <ul>
        <li>Access the personal data we hold about you.</li>
        <li>Correct inaccurate data.</li>
        <li>Request deletion of your data (&ldquo;right to erasure&rdquo;).</li>
        <li>Object to processing or request restriction.</li>
        <li>Withdraw consent for notification emails at any time.</li>
      </ul>
      <p>
        To exercise any of these rights, email{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. We will respond
        within 30 days.
      </p>

      <h3>9. Changes to this policy</h3>
      <p>
        We may update this policy from time to time. The &ldquo;Last
        updated&rdquo; date at the top will reflect any changes. Continued use
        of Match Mixer after changes constitutes acceptance.
      </p>

      <h3>10. Contact</h3>
      <p>
        Questions? Email us at{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </p>
    </div>
  );
}

function TermsOfService() {
  return (
    <div className="legal-body">
      <h2>Terms of Service</h2>
      <p className="legal-meta">Last updated: {EFFECTIVE_DATE}</p>

      <h3>1. Acceptance</h3>
      <p>
        By creating an account or using Match Mixer (&ldquo;the Service&rdquo;)
        at <a href={APP_URL}>{APP_URL}</a>, you agree to these Terms. If you do
        not agree, please do not use the Service.
      </p>

      <h3>2. The Service</h3>
      <p>
        Match Mixer is a free, entertainment-only football prediction game. It
        does not involve real money, gambling, or prizes. All predictions,
        points, and league standings are for fun only and have no monetary
        value.
      </p>

      <h3>3. Eligibility</h3>
      <p>
        You must be at least 13 years old to use Match Mixer. By using the
        Service you confirm that you meet this requirement.
      </p>

      <h3>4. Your account</h3>
      <ul>
        <li>
          You are responsible for keeping your account credentials secure.
        </li>
        <li>
          You must not share your account or allow others to access it on your
          behalf.
        </li>
        <li>
          You must not use a display name that is offensive, misleading, or
          impersonates another person.
        </li>
      </ul>

      <h3>5. Acceptable use</h3>
      <p>You must not:</p>
      <ul>
        <li>Attempt to manipulate or exploit the scoring system.</li>
        <li>
          Interfere with, disrupt, or attempt to gain unauthorised access to the
          Service or its infrastructure.
        </li>
        <li>
          Use automated tools, bots, or scripts to submit predictions or
          interact with the Service, except as expressly permitted.
        </li>
        <li>
          Post or transmit any content that is unlawful, offensive, or harmful.
        </li>
      </ul>

      <h3>6. Intellectual property</h3>
      <p>
        MATCH MIXER&reg; is a registered trade mark. All content, design, and
        code in Match Mixer is owned by or licensed to us. You may not
        reproduce, distribute, or create derivative works without our written
        permission. Club crests, competition logos, and fixture data are the
        property of their respective rights holders.
      </p>

      <h3>7. Disclaimer of warranties</h3>
      <p>
        The Service is provided &ldquo;as is&rdquo; without any warranty,
        express or implied. We do not guarantee that the Service will be
        uninterrupted, error-free, or that fixture data will always be accurate
        or up to date.
      </p>

      <h3>8. Limitation of liability</h3>
      <p>
        To the fullest extent permitted by law, we shall not be liable for any
        indirect, incidental, or consequential damages arising from your use of
        Match Mixer. Our total liability for any claim shall not exceed
        &pound;0, as the Service is provided free of charge.
      </p>

      <h3>9. Termination</h3>
      <p>
        We reserve the right to suspend or terminate any account that violates
        these Terms, at our sole discretion and without notice. You may delete
        your account at any time by contacting{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </p>

      <h3>10. Governing law</h3>
      <p>
        These Terms are governed by the laws of England and Wales. Any disputes
        shall be subject to the exclusive jurisdiction of the courts of England
        and Wales.
      </p>

      <h3>11. Changes</h3>
      <p>
        We may update these Terms from time to time. The &ldquo;Last
        updated&rdquo; date at the top will reflect any changes. Continued use
        of Match Mixer after changes constitutes acceptance.
      </p>

      <h3>12. Contact</h3>
      <p>
        Questions? Email us at{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </p>
    </div>
  );
}

export default function Legal({ page, onClose }) {
  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // Prevent background scroll while open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <div
      className="legal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={page === "privacy" ? "Privacy Policy" : "Terms of Service"}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="legal-modal">
        <button className="legal-close" onClick={onClose} aria-label="Close">
          ✕
        </button>
        {page === "privacy" ? <PrivacyPolicy /> : <TermsOfService />}
      </div>
    </div>
  );
}
